// src/pages/Scanner/Scanner.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  alpacaService,
  ScanResult,
  SmartScanResult,
  SmartScanFilter,
  Position,
  Account,
} from '../../services/AlpacaService';
import { useAccountMode } from '../../context/AccountModeContext';
import { useAuth } from '../../context/AuthContext';
import { ParamSlider } from '../../components/ParamSlider/ParamSlider';
import TICKER_DESC from '../../data/tickerDescriptions';
import styles from './Scanner.module.css';

/* ── Market hours helper ── */

function isMarketOpen(): boolean {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short',
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date());
  const h = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
  const m = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);
  const day = parts.find((p) => p.type === 'weekday')?.value || '';
  if (['Sat', 'Sun'].includes(day)) return false;
  const t = h * 60 + m;
  return t >= 570 && t < 960;
}

/* ── Presets ── */

const RSI_PRESETS: Record<string, { label: string; tickers: string }> = {
  broad: { label: 'Broad Market', tickers: 'SPY,QQQ,DIA,IWM' },
  sectors: { label: 'Sectors', tickers: 'XLK,XLV,XLY,XLE,XLF,XLI,XLC,XLU,XLB,SMH,XBI,CIBR,ICLN' },
  tech: { label: 'Tech Giants', tickers: 'AAPL,MSFT,NVDA,GOOGL,META,AMZN,TSLA' },
  semis: { label: 'Semiconductors', tickers: 'SMH,SOXX,TSM,ASML,AMAT,LRCX,KLAC,MRVL,NVDA,AMD,AVGO,QCOM,INTC' },
  aiCloud: { label: 'AI & Cloud', tickers: 'PLTR,SNOW,DDOG,NET,MDB,AI,PATH,CRM,NOW,ORCL' },
  cyber: { label: 'Cybersecurity', tickers: 'CIBR,HACK,CRWD,PANW,FTNT,ZS' },
  biotech: { label: 'Biotech', tickers: 'XBI,IBB,MRNA,REGN,VRTX,GILD,BIIB' },
  crypto: { label: 'Crypto-Adjacent', tickers: 'MSTR,COIN,MARA,RIOT,CLSK' },
  china: { label: 'China & EM', tickers: 'FXI,KWEB,EEM,BABA,JD,PDD,NIO' },
  cleanEnergy: { label: 'EV & Clean Energy', tickers: 'ICLN,TAN,RIVN,LCID,ENPH,SEDG,PLUG,TSLA' },
  allEtfs: {
    label: 'All ETFs',
    tickers: 'SPY,QQQ,DIA,IWM,XLK,XLV,XLY,XLE,XLF,GLD,TLT,SLV,ITA,FXI,ARKK,XBI,SMH,SOXX,CIBR,ICLN,MSOS',
  },
  hormuzWinners: {
    label: '🛡 Hormuz Winners',
    tickers: 'LMT,RTX,NOC,GD,BA,XOM,CVX,COP,OXY,SLB,LNG,EQT,FRO,STNG,MOS,NTR,CF,AA,NEM,GOLD,GLD,GDX',
  },
  hormuzLosers: { label: '⚠ Hormuz Losers', tickers: 'DAL,UAL,AAL,F,GM,INTC,AMAT,NKE,WMT,AMZN,EWJ,EWY,EEM,FCX,ADM' },
};

const SMART_PRESETS: Record<string, { label: string; tickers: string }> = {
  sectorEtfs: { label: 'S&P Sector ETFs', tickers: 'XLK,XLV,XLY,XLE,XLF,XLI,XLC,XLU,XLB,XLRE,XLP' },
  thematicEtfs: { label: 'Thematic ETFs', tickers: 'SMH,SOXX,XBI,IBB,CIBR,HACK,ICLN,TAN,MSOS,FXI,KWEB,GDX,ITA' },
  broadEtfs: { label: 'Broad ETFs', tickers: 'SPY,QQQ,DIA,IWM,XLK,XLV,XLY,XLE,XLF,XLI,XLC,XLU,GLD,TLT,SLV' },
  top30: {
    label: 'Top 30 Stocks',
    tickers:
      'AAPL,MSFT,NVDA,GOOGL,META,AMZN,TSLA,JPM,V,MA,UNH,HD,PG,JNJ,WMT,BAC,CRM,NFLX,COST,ABBV,MRK,CVX,XOM,PEP,KO,AVGO,TMO,ACN,LLY,ORCL',
  },
  energy: { label: 'Energy', tickers: 'XLE,XOP,OIH,USO,UCO,CVX,XOM,OXY,COP,DVN,SLB,HAL,MPC,VLO,PSX' },
  semis: { label: 'Semiconductors', tickers: 'SMH,SOXX,TSM,ASML,NVDA,AMD,AVGO,AMAT,LRCX,KLAC,MRVL,QCOM,INTC' },
  aiCloud: { label: 'AI & Cloud', tickers: 'PLTR,SNOW,DDOG,NET,MDB,AI,PATH,CRM,NOW,ORCL,AMZN,MSFT,GOOGL' },
  cyber: { label: 'Cybersecurity', tickers: 'CIBR,HACK,CRWD,PANW,FTNT,ZS' },
  biotech: { label: 'Biotech', tickers: 'XBI,IBB,MRNA,REGN,VRTX,GILD,BIIB' },
  crypto: { label: 'Crypto-Adjacent', tickers: 'MSTR,COIN,MARA,RIOT,CLSK' },
  china: { label: 'China & EM', tickers: 'FXI,KWEB,EEM,BABA,JD,PDD,NIO' },
  cleanEnergy: { label: 'EV & Clean Energy', tickers: 'ICLN,TAN,RIVN,LCID,ENPH,SEDG,PLUG,TSLA' },
  materials: { label: 'Materials & Mining', tickers: 'XLB,AA,FCX,NUE,CLF,X,NEM,GOLD,GDX' },
  cannabis: { label: 'Cannabis', tickers: 'MSOS,TLRY,CGC,ACB,CRON' },
  allEtfs: {
    label: 'All ETFs (50+)',
    tickers:
      'SPY,QQQ,DIA,IWM,XLK,XLV,XLY,XLE,XLF,XLI,XLC,XLU,XLB,XLRE,XLP,GLD,SLV,TLT,IEF,HYG,LQD,VXX,ARKK,XBI,IBB,ITA,FXI,EEM,EFA,KWEB,SMH,SOXX,CIBR,HACK,ICLN,TAN,MSOS,XHB,XRT,KRE,GDX,GDXJ,USO,UNG,DBA,JETS',
  },
  hormuzAll: {
    label: '🌍 Hormuz Plays',
    tickers:
      'LMT,RTX,NOC,GD,BA,XOM,CVX,COP,OXY,SLB,LNG,EQT,FRO,STNG,MOS,NTR,CF,AA,NEM,GOLD,GLD,GDX,DAL,UAL,AAL,F,GM,INTC,AMAT,NKE,WMT,EWJ,EWY,EEM',
  },
  defense: { label: '🛡 Defense', tickers: 'LMT,RTX,NOC,GD,BA,HII,LDOS,CACI,ITA' },
  shipping: { label: '🚢 Shipping', tickers: 'FRO,STNG,DHT,INSW,TEN,ZIM,MATX' },
  hormuzLosers: {
    label: '⚠ Hormuz Shorts',
    tickers: 'DAL,UAL,AAL,LUV,F,GM,RIVN,INTC,AMAT,LRCX,NKE,WMT,AMZN,EWJ,EWY,EEM,FCX,ADM,BG',
  },
};

const FILTER_PRESETS: Record<string, { label: string; desc: string; filters: SmartScanFilter }> = {
  bounceHunter: {
    label: 'Bounce Hunter',
    desc: 'Find deeply oversold stocks in uptrends with high volume',
    filters: {
      rsi2_oversold_enabled: true,
      rsi2_below: 10,
      rsi14_enabled: true,
      rsi14_min: 40,
      rsi14_max: 100,
      rvol_enabled: true,
      rvol_min: 1.5,
      vwap_enabled: true,
      vwap_position: 'below',
    },
  },
  momentum: {
    label: 'Momentum',
    desc: 'Strong stocks breaking out with volume confirmation',
    filters: {
      rsi2_overbought_enabled: true,
      rsi2_above: 75,
      macd_enabled: true,
      macd_signal: 'bullish',
      rvol_enabled: true,
      rvol_min: 2.0,
      vwap_enabled: true,
      vwap_position: 'above',
    },
  },
  meanReversion: {
    label: 'Mean Reversion',
    desc: 'Oversold with extreme price moves below Bollinger Bands',
    filters: {
      rsi2_oversold_enabled: true,
      rsi2_below: 15,
      bb_enabled: true,
      bb_below: 0,
      atr_move_enabled: true,
      atr_move_min: 1.5,
    },
  },
};

type SortKey = 'symbol' | 'price' | 'change_pct' | 'rsi2' | 'rsi6' | 'rsi14' | 'volume';
type SmartSortKey = 'symbol' | 'price' | 'change_pct' | 'composite_score' | 'rsi2' | 'rel_vol';

/* ── Default Smart Filter State ── */
const defaultFilters: SmartScanFilter = {
  rsi2_oversold_enabled: false,
  rsi2_below: 10,
  rsi2_overbought_enabled: false,
  rsi2_above: 90,
  rsi14_enabled: false,
  rsi14_min: 30,
  rsi14_max: 70,
  rvol_enabled: false,
  rvol_min: 1.5,
  atr_move_enabled: false,
  atr_move_min: 1.5,
  vwap_enabled: false,
  vwap_position: 'below',
  macd_enabled: false,
  macd_signal: 'bullish',
  bb_enabled: false,
  bb_below: 0,
};

/* ── Component ── */

const Scanner: React.FC = () => {
  const { mode, isLive } = useAccountMode();
  const { isAdmin } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState<'rsi' | 'smart'>('rsi');

  // RSI Scanner state
  const [tickerInput, setTickerInput] = useState('SPY,QQQ,DIA,IWM,XLK,XLV,XLE,XLF');
  const [results, setResults] = useState<ScanResult[]>([]);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [lastScan, setLastScan] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('rsi2');
  const [sortAsc, setSortAsc] = useState(true);

  // Smart Scanner state
  const [smartTickers, setSmartTickers] = useState('XLK,XLV,XLY,XLE,XLF,XLI,XLC,XLU,XLB,XLRE,XLP');
  const [smartFilters, setSmartFilters] = useState<SmartScanFilter>({ ...defaultFilters });
  const [smartResults, setSmartResults] = useState<SmartScanResult[]>([]);
  const [smartScanning, setSmartScanning] = useState(false);
  const [smartError, setSmartError] = useState('');
  const [smartLastScan, setSmartLastScan] = useState('');
  const [smartTotalScanned, setSmartTotalScanned] = useState(0);
  const [smartFiltersApplied, setSmartFiltersApplied] = useState<string[]>([]);
  const [smartSortKey, setSmartSortKey] = useState<SmartSortKey>('composite_score');
  const [smartSortAsc, setSmartSortAsc] = useState(false);

  // Shared state
  const [orderModal, setOrderModal] = useState<{
    symbol: string;
    side: 'buy' | 'sell';
    price: number;
    signal: string;
  } | null>(null);
  const [orderAmount, setOrderAmount] = useState(500);
  const [orderType, setOrderType] = useState<'notional' | 'qty'>('notional');
  const [orderQty, setOrderQty] = useState(10);
  const [orderInProgress, setOrderInProgress] = useState(false);
  const [liveConfirmed, setLiveConfirmed] = useState(false);
  const [positions, setPositions] = useState<Position[]>([]);
  const [account, setAccount] = useState<Account | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  const showToast = (message: string, type: 'success' | 'error') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  };

  /* ── RSI Scanner ── */
  const runScan = useCallback(async () => {
    const tickers = tickerInput.trim();
    if (!tickers) {
      setScanError('Enter at least one ticker');
      return;
    }
    setScanning(true);
    setScanError('');
    try {
      const res = await alpacaService.scanRsi(tickers);
      setResults(res.results);
      setLastScan(new Date().toLocaleTimeString());
    } catch (e: any) {
      setScanError(e.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, [tickerInput]);

  useEffect(() => {
    if (activeTab !== 'rsi' || results.length === 0) return;
    const interval = isMarketOpen() ? 15_000 : 60_000;
    const id = setInterval(runScan, interval);
    return () => clearInterval(id);
  }, [activeTab, results.length, runScan]);

  /* ── Smart Scanner ── */
  const runSmartScan = useCallback(async () => {
    const tickers = smartTickers.trim();
    if (!tickers) {
      setSmartError('Select a universe or enter tickers');
      return;
    }
    const anyEnabled = Object.keys(smartFilters).some(
      (k) => k.endsWith('_enabled') && smartFilters[k as keyof SmartScanFilter],
    );
    if (!anyEnabled) {
      setSmartError('Enable at least one filter');
      return;
    }
    setSmartScanning(true);
    setSmartError('');
    try {
      const res = await alpacaService.smartScan(tickers, smartFilters);
      setSmartResults(res.results);
      setSmartTotalScanned(res.total_scanned);
      setSmartFiltersApplied(res.filters_applied);
      setSmartLastScan(new Date().toLocaleTimeString());
    } catch (e: any) {
      setSmartError(e.message || 'Smart scan failed');
    } finally {
      setSmartScanning(false);
    }
  }, [smartTickers, smartFilters]);

  /* ── Positions ── */
  const fetchPositions = useCallback(async () => {
    try {
      const [posData, acct] = await Promise.all([alpacaService.getPositions(mode), alpacaService.getAccount(mode)]);
      setPositions(posData.positions || []);
      setAccount(acct);
    } catch (err: any) {
      showToast(err.message || 'Failed to load positions', 'error');
    }
  }, [mode]);

  useEffect(() => {
    fetchPositions();
    const id = setInterval(fetchPositions, 30_000);
    return () => clearInterval(id);
  }, [fetchPositions]);

  /* ── Sort helpers ── */
  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(key === 'symbol');
    }
  };

  const sortedResults = [...results].sort((a, b) => {
    const av = a[sortKey] ?? 0;
    const bv = b[sortKey] ?? 0;
    if (typeof av === 'string' && typeof bv === 'string') return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
  });

  const handleSmartSort = (key: SmartSortKey) => {
    if (key === smartSortKey) setSmartSortAsc(!smartSortAsc);
    else {
      setSmartSortKey(key);
      setSmartSortAsc(key === 'symbol');
    }
  };

  const sortedSmartResults = [...smartResults].sort((a, b) => {
    const av = a[smartSortKey] ?? 0;
    const bv = b[smartSortKey] ?? 0;
    if (typeof av === 'string' && typeof bv === 'string')
      return smartSortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    return smartSortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
  });

  /* ── Order handling ── */
  const handlePlaceOrder = async () => {
    if (!orderModal) return;
    if (isLive && !liveConfirmed) return;
    setOrderInProgress(true);
    try {
      const res = await alpacaService.placeOrder({
        symbol: orderModal.symbol,
        side: orderModal.side,
        ...(orderType === 'notional' ? { notional: orderAmount } : { qty: orderQty }),
        mode,
      });
      if (res.success) {
        showToast(
          `${orderModal.side.toUpperCase()} ${orderModal.symbol} — ${res.order?.status || 'submitted'}`,
          'success',
        );
        setOrderModal(null);
        setLiveConfirmed(false);
        fetchPositions();
      } else {
        showToast(res.error || 'Order failed', 'error');
      }
    } catch (e: any) {
      showToast(e.message || 'Order failed', 'error');
    } finally {
      setOrderInProgress(false);
    }
  };

  const handleClosePosition = async (symbol: string) => {
    try {
      await alpacaService.closePosition(symbol, mode);
      showToast(`Closed ${symbol}`, 'success');
      fetchPositions();
    } catch (e: any) {
      showToast(e.message || 'Close failed', 'error');
    }
  };

  /* ── Helpers ── */
  const rsiColor = (val: number | null) => {
    if (val === null) return undefined;
    if (val < 20) return 'var(--color-positive)';
    if (val < 30) return '#8bc34a';
    if (val > 80) return 'var(--color-negative)';
    if (val > 70) return '#ff9800';
    return 'var(--text-secondary)';
  };

  const signalBadge = (signal: string) => {
    switch (signal) {
      case 'OVERSOLD':
        return styles.signalOversold;
      case 'WEAK':
        return styles.signalWeak;
      case 'OVERBOUGHT':
        return styles.signalOverbought;
      case 'STRONG':
        return styles.signalStrong;
      default:
        return styles.signalNeutral;
    }
  };

  const directionBadge = (dir: string) => {
    switch (dir) {
      case 'BULL':
        return styles.dirBull;
      case 'BEAR':
        return styles.dirBear;
      default:
        return styles.dirChop;
    }
  };

  const scoreColor = (score: number) => {
    if (score >= 2) return 'var(--color-positive)';
    if (score >= 0.5) return '#8bc34a';
    if (score <= -2) return 'var(--color-negative)';
    if (score <= -0.5) return '#ff9800';
    return 'var(--text-secondary)';
  };

  const allSymbols =
    activeTab === 'rsi' ? new Set(results.map((r) => r.symbol)) : new Set(smartResults.map((r) => r.symbol));
  const relevantPositions = positions.filter((p) => p.asset_class === 'us_equity' && allSymbols.has(p.symbol));

  const sortArrow = (key: string, currentKey: string, asc: boolean) => {
    if (currentKey !== key) return '';
    return asc ? ' ▲' : ' ▼';
  };

  const updateFilter = (key: keyof SmartScanFilter, value: any) => {
    setSmartFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilterPreset = (key: string) => {
    setSmartFilters({ ...defaultFilters, ...FILTER_PRESETS[key].filters });
  };

  return (
    <section className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Market Scanner</h1>
        <p className={styles.subtitle}>Scan stocks for trading signals and trade directly.</p>
      </div>

      {/* Tab bar */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tabBtn} ${activeTab === 'rsi' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('rsi')}
        >
          RSI Scanner
        </button>
        <button
          className={`${styles.tabBtn} ${activeTab === 'smart' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('smart')}
        >
          Smart Scanner
        </button>
      </div>

      {/* Account mode indicator */}
      <div className={`${styles.modeBadge} ${isLive ? styles.modeLive : styles.modePaper}`}>
        {isLive ? 'LIVE' : 'PAPER'} Account
        {account && (
          <span className={styles.modeInfo}>
            {' '}
            · Buying Power: ${Number(account.buying_power).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        )}
      </div>

      {/* ════════════════════ RSI SCANNER TAB ════════════════════ */}
      {activeTab === 'rsi' && (
        <>
          {/* Ticker Input */}
          <div className={styles.card}>
            <div className={styles.scanRow}>
              <input
                className={styles.tickerInput}
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && runScan()}
                placeholder="Enter tickers: AAPL, TSLA, NVDA, SPY..."
                title="Ticker symbols for RSI scan"
              />
              <button className={styles.scanBtn} onClick={runScan} disabled={scanning}>
                {scanning ? 'Scanning...' : 'Scan'}
              </button>
            </div>
            <div className={styles.presetRow}>
              {Object.entries(RSI_PRESETS).map(([key, p]) => (
                <button key={key} className={styles.presetBtn} onClick={() => setTickerInput(p.tickers)}>
                  {p.label}
                </button>
              ))}
            </div>
            {scanError && <div className={styles.error}>{scanError}</div>}
          </div>

          {/* RSI Results */}
          {results.length > 0 && (
            <div className={styles.card}>
              <div className={styles.resultHeader}>
                <h2 className={styles.cardTitle}>
                  Scan Results
                  <span className={styles.resultCount}>{results.length} symbols</span>
                </h2>
                {lastScan && <span className={styles.lastScan}>Last scan: {lastScan}</span>}
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('symbol')}>Symbol{sortArrow('symbol', sortKey, sortAsc)}</th>
                      <th onClick={() => handleSort('price')}>Price{sortArrow('price', sortKey, sortAsc)}</th>
                      <th onClick={() => handleSort('change_pct')}>Chg%{sortArrow('change_pct', sortKey, sortAsc)}</th>
                      <th onClick={() => handleSort('rsi2')}>RSI(2){sortArrow('rsi2', sortKey, sortAsc)}</th>
                      <th onClick={() => handleSort('rsi6')}>RSI(6){sortArrow('rsi6', sortKey, sortAsc)}</th>
                      <th onClick={() => handleSort('rsi14')}>RSI(14){sortArrow('rsi14', sortKey, sortAsc)}</th>
                      <th onClick={() => handleSort('volume')}>Volume{sortArrow('volume', sortKey, sortAsc)}</th>
                      <th>Signal</th>
                      {isAdmin && <th>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedResults.map((r) => (
                      <tr key={r.symbol}>
                        <td className={styles.symbolCell}>
                          {r.symbol}
                          {TICKER_DESC[r.symbol] && <span className={styles.symbolDesc}>{TICKER_DESC[r.symbol]}</span>}
                        </td>
                        <td>${r.price?.toFixed(2)}</td>
                        <td style={{ color: r.change_pct >= 0 ? 'var(--color-positive)' : 'var(--color-negative)' }}>
                          {r.change_pct >= 0 ? '+' : ''}
                          {r.change_pct?.toFixed(2)}%
                        </td>
                        <td style={{ color: rsiColor(r.rsi2), fontWeight: 700 }}>{r.rsi2?.toFixed(1) ?? '—'}</td>
                        <td style={{ color: rsiColor(r.rsi6) }}>{r.rsi6?.toFixed(1) ?? '—'}</td>
                        <td style={{ color: rsiColor(r.rsi14) }}>{r.rsi14?.toFixed(1) ?? '—'}</td>
                        <td className={styles.volumeCell}>
                          {r.volume
                            ? r.volume >= 1_000_000
                              ? `${(r.volume / 1_000_000).toFixed(1)}M`
                              : `${(r.volume / 1_000).toFixed(0)}K`
                            : '—'}
                        </td>
                        <td>
                          <span className={`${styles.signalBadge} ${signalBadge(r.signal)}`}>{r.signal}</span>
                        </td>
                        {isAdmin && (
                          <td className={styles.actionCell}>
                            {(r.signal === 'OVERSOLD' || r.signal === 'WEAK') && (
                              <button
                                className={styles.longBtn}
                                onClick={() =>
                                  setOrderModal({ symbol: r.symbol, side: 'buy', price: r.price, signal: r.signal })
                                }
                              >
                                Long
                              </button>
                            )}
                            {(r.signal === 'OVERBOUGHT' || r.signal === 'STRONG') && (
                              <button
                                className={styles.shortBtn}
                                onClick={() =>
                                  setOrderModal({ symbol: r.symbol, side: 'sell', price: r.price, signal: r.signal })
                                }
                              >
                                Short
                              </button>
                            )}
                            {r.signal === 'NEUTRAL' && <span className={styles.neutralAction}>—</span>}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className={styles.mobileCards}>
                {sortedResults.map((r) => (
                  <div key={r.symbol} className={styles.mobileCard}>
                    <div className={styles.mobileCardHeader}>
                      <span className={styles.symbolCell}>
                        {r.symbol}
                        {TICKER_DESC[r.symbol] && <span className={styles.symbolDesc}>{TICKER_DESC[r.symbol]}</span>}
                      </span>
                      <span className={`${styles.signalBadge} ${signalBadge(r.signal)}`}>{r.signal}</span>
                    </div>
                    <div className={styles.mobileCardBody}>
                      <div className={styles.mobileCardRow}>
                        <span>Price</span>
                        <span>${r.price?.toFixed(2)}</span>
                      </div>
                      <div className={styles.mobileCardRow}>
                        <span>Change</span>
                        <span style={{ color: r.change_pct >= 0 ? 'var(--color-positive)' : 'var(--color-negative)' }}>
                          {r.change_pct >= 0 ? '+' : ''}
                          {r.change_pct?.toFixed(2)}%
                        </span>
                      </div>
                      <div className={styles.mobileCardRow}>
                        <span>RSI(2)</span>
                        <span style={{ color: rsiColor(r.rsi2), fontWeight: 700 }}>{r.rsi2?.toFixed(1) ?? '—'}</span>
                      </div>
                      <div className={styles.mobileCardRow}>
                        <span>RSI(6)</span>
                        <span style={{ color: rsiColor(r.rsi6) }}>{r.rsi6?.toFixed(1) ?? '—'}</span>
                      </div>
                      <div className={styles.mobileCardRow}>
                        <span>RSI(14)</span>
                        <span style={{ color: rsiColor(r.rsi14) }}>{r.rsi14?.toFixed(1) ?? '—'}</span>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className={styles.mobileCardActions}>
                        {(r.signal === 'OVERSOLD' || r.signal === 'WEAK') && (
                          <button
                            className={styles.longBtn}
                            onClick={() =>
                              setOrderModal({ symbol: r.symbol, side: 'buy', price: r.price, signal: r.signal })
                            }
                          >
                            Long
                          </button>
                        )}
                        {(r.signal === 'OVERBOUGHT' || r.signal === 'STRONG') && (
                          <button
                            className={styles.shortBtn}
                            onClick={() =>
                              setOrderModal({ symbol: r.symbol, side: 'sell', price: r.price, signal: r.signal })
                            }
                          >
                            Short
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ════════════════════ SMART SCANNER TAB ════════════════════ */}
      {activeTab === 'smart' && (
        <>
          {/* Filter Presets */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Quick Presets</h2>
            <p className={styles.filterDesc}>Pre-configured filter combinations for common strategies</p>
            <div className={styles.filterPresetRow}>
              {Object.entries(FILTER_PRESETS).map(([key, p]) => (
                <button
                  key={key}
                  className={styles.filterPresetBtn}
                  onClick={() => applyFilterPreset(key)}
                  title={p.desc}
                >
                  {p.label}
                </button>
              ))}
              <button
                className={`${styles.filterPresetBtn} ${styles.filterPresetReset}`}
                onClick={() => setSmartFilters({ ...defaultFilters })}
              >
                Reset All
              </button>
            </div>
          </div>

          {/* Scan Filters */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Scan Filters</h2>
            <p className={styles.filterDesc}>
              Enable filters to narrow down results. Only stocks matching ALL active filters will appear.
            </p>

            <div className={styles.filterGrid}>
              {/* RSI(2) Oversold */}
              <div className={`${styles.filterRow} ${smartFilters.rsi2_oversold_enabled ? styles.filterActive : ''}`}>
                <label className={styles.filterCheck}>
                  <input
                    type="checkbox"
                    checked={!!smartFilters.rsi2_oversold_enabled}
                    onChange={(e) => updateFilter('rsi2_oversold_enabled', e.target.checked)}
                    aria-label="RSI(2) Oversold filter"
                  />
                  RSI(2) Oversold
                </label>
                {smartFilters.rsi2_oversold_enabled && (
                  <ParamSlider
                    label="Below"
                    value={smartFilters.rsi2_below ?? 10}
                    min={1}
                    max={30}
                    step={1}
                    onChange={(v) => updateFilter('rsi2_below', v)}
                  />
                )}
              </div>

              {/* RSI(2) Overbought */}
              <div className={`${styles.filterRow} ${smartFilters.rsi2_overbought_enabled ? styles.filterActive : ''}`}>
                <label className={styles.filterCheck}>
                  <input
                    type="checkbox"
                    checked={!!smartFilters.rsi2_overbought_enabled}
                    onChange={(e) => updateFilter('rsi2_overbought_enabled', e.target.checked)}
                    aria-label="RSI(2) Overbought filter"
                  />
                  RSI(2) Overbought
                </label>
                {smartFilters.rsi2_overbought_enabled && (
                  <ParamSlider
                    label="Above"
                    value={smartFilters.rsi2_above ?? 90}
                    min={70}
                    max={99}
                    step={1}
                    onChange={(v) => updateFilter('rsi2_above', v)}
                  />
                )}
              </div>

              {/* RSI(6) Oversold */}
              <div className={`${styles.filterRow} ${smartFilters.rsi6_oversold_enabled ? styles.filterActive : ''}`}>
                <label className={styles.filterCheck}>
                  <input
                    type="checkbox"
                    checked={!!smartFilters.rsi6_oversold_enabled}
                    onChange={(e) => updateFilter('rsi6_oversold_enabled', e.target.checked)}
                    aria-label="RSI(6) Oversold filter"
                  />
                  RSI(6) Oversold
                </label>
                {smartFilters.rsi6_oversold_enabled && (
                  <ParamSlider
                    label="Below"
                    value={smartFilters.rsi6_below ?? 30}
                    min={10}
                    max={50}
                    step={1}
                    onChange={(v) => updateFilter('rsi6_below', v)}
                  />
                )}
              </div>

              {/* RSI(6) Overbought */}
              <div className={`${styles.filterRow} ${smartFilters.rsi6_overbought_enabled ? styles.filterActive : ''}`}>
                <label className={styles.filterCheck}>
                  <input
                    type="checkbox"
                    checked={!!smartFilters.rsi6_overbought_enabled}
                    onChange={(e) => updateFilter('rsi6_overbought_enabled', e.target.checked)}
                    aria-label="RSI(6) Overbought filter"
                  />
                  RSI(6) Overbought
                </label>
                {smartFilters.rsi6_overbought_enabled && (
                  <ParamSlider
                    label="Above"
                    value={smartFilters.rsi6_above ?? 70}
                    min={50}
                    max={90}
                    step={1}
                    onChange={(v) => updateFilter('rsi6_above', v)}
                  />
                )}
              </div>

              {/* RSI(14) Range */}
              <div className={`${styles.filterRow} ${smartFilters.rsi14_enabled ? styles.filterActive : ''}`}>
                <label className={styles.filterCheck}>
                  <input
                    type="checkbox"
                    checked={!!smartFilters.rsi14_enabled}
                    onChange={(e) => updateFilter('rsi14_enabled', e.target.checked)}
                    aria-label="RSI(14) Range filter"
                  />
                  RSI(14) Range
                </label>
                {smartFilters.rsi14_enabled && (
                  <>
                    <ParamSlider
                      label="Min"
                      value={smartFilters.rsi14_min ?? 30}
                      min={0}
                      max={100}
                      step={5}
                      onChange={(v) => updateFilter('rsi14_min', v)}
                    />
                    <ParamSlider
                      label="Max"
                      value={smartFilters.rsi14_max ?? 70}
                      min={0}
                      max={100}
                      step={5}
                      onChange={(v) => updateFilter('rsi14_max', v)}
                    />
                  </>
                )}
              </div>

              {/* Relative Volume */}
              <div className={`${styles.filterRow} ${smartFilters.rvol_enabled ? styles.filterActive : ''}`}>
                <label className={styles.filterCheck}>
                  <input
                    type="checkbox"
                    checked={!!smartFilters.rvol_enabled}
                    onChange={(e) => updateFilter('rvol_enabled', e.target.checked)}
                    aria-label="Relative Volume filter"
                  />
                  Relative Volume
                </label>
                {smartFilters.rvol_enabled && (
                  <ParamSlider
                    label="Min"
                    value={smartFilters.rvol_min ?? 1.5}
                    min={0.5}
                    max={5}
                    step={0.1}
                    unit="x"
                    onChange={(v) => updateFilter('rvol_min', v)}
                  />
                )}
              </div>

              {/* ATR Move */}
              <div className={`${styles.filterRow} ${smartFilters.atr_move_enabled ? styles.filterActive : ''}`}>
                <label className={styles.filterCheck}>
                  <input
                    type="checkbox"
                    checked={!!smartFilters.atr_move_enabled}
                    onChange={(e) => updateFilter('atr_move_enabled', e.target.checked)}
                    aria-label="ATR Move filter"
                  />
                  ATR Move
                </label>
                {smartFilters.atr_move_enabled && (
                  <ParamSlider
                    label="Min"
                    value={smartFilters.atr_move_min ?? 1.5}
                    min={0.5}
                    max={4}
                    step={0.1}
                    unit="x ATR"
                    onChange={(v) => updateFilter('atr_move_min', v)}
                  />
                )}
              </div>

              {/* VWAP Position */}
              <div className={`${styles.filterRow} ${smartFilters.vwap_enabled ? styles.filterActive : ''}`}>
                <label className={styles.filterCheck}>
                  <input
                    type="checkbox"
                    checked={!!smartFilters.vwap_enabled}
                    onChange={(e) => updateFilter('vwap_enabled', e.target.checked)}
                    aria-label="VWAP Position filter"
                  />
                  VWAP Position
                </label>
                {smartFilters.vwap_enabled && (
                  <div className={styles.filterSelect}>
                    <select
                      value={smartFilters.vwap_position ?? 'below'}
                      onChange={(e) => updateFilter('vwap_position', e.target.value)}
                      title="VWAP position filter"
                    >
                      <option value="below">Price Below VWAP</option>
                      <option value="above">Price Above VWAP</option>
                    </select>
                  </div>
                )}
              </div>

              {/* MACD Signal */}
              <div className={`${styles.filterRow} ${smartFilters.macd_enabled ? styles.filterActive : ''}`}>
                <label className={styles.filterCheck}>
                  <input
                    type="checkbox"
                    checked={!!smartFilters.macd_enabled}
                    onChange={(e) => updateFilter('macd_enabled', e.target.checked)}
                    aria-label="MACD Signal filter"
                  />
                  MACD Signal
                </label>
                {smartFilters.macd_enabled && (
                  <div className={styles.filterSelect}>
                    <select
                      value={smartFilters.macd_signal ?? 'bullish'}
                      onChange={(e) => updateFilter('macd_signal', e.target.value)}
                      title="MACD signal filter"
                    >
                      <option value="bullish">Bullish (histogram +)</option>
                      <option value="bearish">Bearish (histogram −)</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Bollinger %B */}
              <div className={`${styles.filterRow} ${smartFilters.bb_enabled ? styles.filterActive : ''}`}>
                <label className={styles.filterCheck}>
                  <input
                    type="checkbox"
                    checked={!!smartFilters.bb_enabled}
                    onChange={(e) => updateFilter('bb_enabled', e.target.checked)}
                    aria-label="Bollinger %B filter"
                  />
                  Bollinger %B
                </label>
                {smartFilters.bb_enabled && (
                  <ParamSlider
                    label="Below"
                    value={smartFilters.bb_below ?? 0}
                    min={-0.5}
                    max={1.5}
                    step={0.05}
                    onChange={(v) => updateFilter('bb_below', v)}
                    formatValue={(v) => v.toFixed(2)}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Universe + Scan Button */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Stock Universe</h2>
            <div className={styles.scanRow}>
              <input
                className={styles.tickerInput}
                value={smartTickers}
                onChange={(e) => setSmartTickers(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && runSmartScan()}
                placeholder="Enter tickers or use presets below..."
                title="Ticker symbols for Smart scan"
              />
              <button className={styles.scanBtn} onClick={runSmartScan} disabled={smartScanning}>
                {smartScanning ? 'Scanning...' : 'Smart Scan'}
              </button>
            </div>
            <div className={styles.presetRow}>
              {Object.entries(SMART_PRESETS).map(([key, p]) => (
                <button key={key} className={styles.presetBtn} onClick={() => setSmartTickers(p.tickers)}>
                  {p.label}
                </button>
              ))}
            </div>
            {smartError && <div className={styles.error}>{smartError}</div>}
          </div>

          {/* Smart Results */}
          {smartResults.length > 0 && (
            <div className={styles.card}>
              <div className={styles.resultHeader}>
                <h2 className={styles.cardTitle}>
                  Smart Scan Results
                  <span className={styles.resultCount}>
                    {smartResults.length} match{smartResults.length !== 1 ? 'es' : ''} from {smartTotalScanned} scanned
                  </span>
                </h2>
                {smartLastScan && <span className={styles.lastScan}>Last scan: {smartLastScan}</span>}
              </div>
              {smartFiltersApplied.length > 0 && (
                <div className={styles.appliedFilters}>
                  {smartFiltersApplied.map((f) => (
                    <span key={f} className={styles.appliedBadge}>
                      {f}
                    </span>
                  ))}
                </div>
              )}
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSmartSort('symbol')}>
                        Symbol{sortArrow('symbol', smartSortKey, smartSortAsc)}
                      </th>
                      <th onClick={() => handleSmartSort('price')}>
                        Price{sortArrow('price', smartSortKey, smartSortAsc)}
                      </th>
                      <th onClick={() => handleSmartSort('change_pct')}>
                        Chg%{sortArrow('change_pct', smartSortKey, smartSortAsc)}
                      </th>
                      <th onClick={() => handleSmartSort('composite_score')}>
                        Score{sortArrow('composite_score', smartSortKey, smartSortAsc)}
                      </th>
                      <th>Direction</th>
                      <th>Indicators</th>
                      {isAdmin && <th>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSmartResults.map((r) => (
                      <tr key={r.symbol}>
                        <td className={styles.symbolCell}>
                          {r.symbol}
                          {TICKER_DESC[r.symbol] && <span className={styles.symbolDesc}>{TICKER_DESC[r.symbol]}</span>}
                        </td>
                        <td>${r.price?.toFixed(2)}</td>
                        <td style={{ color: r.change_pct >= 0 ? 'var(--color-positive)' : 'var(--color-negative)' }}>
                          {r.change_pct >= 0 ? '+' : ''}
                          {r.change_pct?.toFixed(2)}%
                        </td>
                        <td>
                          <span className={styles.scoreValue} style={{ color: scoreColor(r.composite_score) }}>
                            {r.composite_score > 0 ? '+' : ''}
                            {r.composite_score.toFixed(1)}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles.dirBadge} ${directionBadge(r.direction)}`}>{r.direction}</span>
                        </td>
                        <td className={styles.indicatorCell}>
                          {r.rsi2 != null && (
                            <span className={styles.indBadge} style={{ color: rsiColor(r.rsi2) }}>
                              RSI2: {r.rsi2}
                            </span>
                          )}
                          {r.rsi6 != null && (
                            <span className={styles.indBadge} style={{ color: rsiColor(r.rsi6) }}>
                              RSI6: {r.rsi6}
                            </span>
                          )}
                          {r.rel_vol != null && (
                            <span
                              className={styles.indBadge}
                              style={{ color: r.rel_vol > 2 ? '#60a5fa' : 'var(--text-tertiary)' }}
                            >
                              RVOL: {r.rel_vol}x
                            </span>
                          )}
                          {r.vwap_position && <span className={styles.indBadge}>VWAP: {r.vwap_position}</span>}
                          {r.macd_signal && (
                            <span
                              className={styles.indBadge}
                              style={{
                                color: r.macd_signal === 'bullish' ? 'var(--color-positive)' : 'var(--color-negative)',
                              }}
                            >
                              MACD: {r.macd_signal}
                            </span>
                          )}
                          {r.bb_pct_b != null && (
                            <span
                              className={styles.indBadge}
                              style={{
                                color:
                                  r.bb_pct_b < 0
                                    ? 'var(--color-positive)'
                                    : r.bb_pct_b > 1
                                      ? 'var(--color-negative)'
                                      : 'var(--text-tertiary)',
                              }}
                            >
                              BB: {r.bb_pct_b.toFixed(2)}
                            </span>
                          )}
                          {r.atr_move != null && <span className={styles.indBadge}>ATR: {r.atr_move}x</span>}
                        </td>
                        {isAdmin && (
                          <td className={styles.actionCell}>
                            {r.recommendation === 'LONG' && (
                              <button
                                className={styles.longBtn}
                                onClick={() =>
                                  setOrderModal({ symbol: r.symbol, side: 'buy', price: r.price, signal: r.signal })
                                }
                              >
                                Long
                              </button>
                            )}
                            {r.recommendation === 'SHORT' && (
                              <button
                                className={styles.shortBtn}
                                onClick={() =>
                                  setOrderModal({ symbol: r.symbol, side: 'sell', price: r.price, signal: r.signal })
                                }
                              >
                                Short
                              </button>
                            )}
                            {r.recommendation === 'NEUTRAL' && <span className={styles.neutralAction}>—</span>}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No results message */}
          {smartLastScan && smartResults.length === 0 && !smartScanning && (
            <div className={styles.card}>
              <p className={styles.noResults}>
                No stocks matched all filters. Try relaxing your criteria or scanning a larger universe.
              </p>
            </div>
          )}
        </>
      )}

      {/* ════════════════════ SHARED: POSITIONS ════════════════════ */}
      {relevantPositions.length > 0 && (
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Your Positions</h2>
          <div className={styles.positionList}>
            {relevantPositions.map((p) => {
              const pnl = parseFloat(p.unrealized_pl);
              return (
                <div key={p.symbol} className={styles.positionRow}>
                  <span className={styles.posSymbol}>{p.symbol}</span>
                  <span className={styles.posSide}>{p.side}</span>
                  <span className={styles.posQty}>
                    {p.qty} shares @ ${parseFloat(p.avg_entry_price).toFixed(2)}
                  </span>
                  <span
                    className={styles.posPnl}
                    style={{ color: pnl >= 0 ? 'var(--color-positive)' : 'var(--color-negative)' }}
                  >
                    {pnl >= 0 ? '+' : ''}
                    {pnl.toFixed(2)}
                  </span>
                  {isAdmin && (
                    <button className={styles.closeBtn} onClick={() => handleClosePosition(p.symbol)}>
                      Close
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ════════════════════ SHARED: ORDER MODAL ════════════════════ */}
      {orderModal &&
        (() => {
          const modalPos = positions.find((p) => p.symbol === orderModal.symbol);
          const isShort = orderModal.side === 'sell';
          const actionLabel = isShort ? 'SHORT' : 'BUY';
          const actionDesc = isShort
            ? `Open a new SHORT position in ${orderModal.symbol}`
            : `Open a new LONG position in ${orderModal.symbol}`;
          return (
            <div
              className={styles.overlay}
              onClick={() => {
                setOrderModal(null);
                setLiveConfirmed(false);
              }}
            >
              <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h3>
                  {actionLabel} {orderModal.symbol}
                </h3>
                <p className={styles.modalDesc}>{actionDesc}</p>
                <p className={styles.modalPrice}>
                  Current price: <strong>${orderModal.price?.toFixed(2)}</strong>
                  <span className={`${styles.signalBadge} ${signalBadge(orderModal.signal)}`} style={{ marginLeft: 8 }}>
                    {orderModal.signal}
                  </span>
                </p>

                {/* Position context */}
                {modalPos && (
                  <div className={styles.modalPositionContext}>
                    You hold {Math.abs(parseFloat(modalPos.qty))} shares {modalPos.side.toUpperCase()}
                  </div>
                )}

                {isShort && isLive && (
                  <div className={styles.shortWarning}>
                    ⚠️ <strong>SHORT on LIVE ACCOUNT</strong> — This opens a new short position with{' '}
                    <strong>unlimited loss potential</strong>.
                  </div>
                )}

                {isLive && !isShort && (
                  <div className={styles.liveWarning}>⚠️ LIVE ACCOUNT — This order will use real money!</div>
                )}

                <div className={styles.modalSection}>
                  <div className={styles.orderTypeToggle}>
                    <button
                      className={`${styles.orderTypeBtn} ${orderType === 'notional' ? styles.orderTypeActive : ''}`}
                      onClick={() => setOrderType('notional')}
                    >
                      $ Amount
                    </button>
                    <button
                      className={`${styles.orderTypeBtn} ${orderType === 'qty' ? styles.orderTypeActive : ''}`}
                      onClick={() => setOrderType('qty')}
                    >
                      Shares
                    </button>
                  </div>

                  {orderType === 'notional' ? (
                    <div className={styles.amountRow}>
                      <label className={styles.amountLabel}>Amount ($)</label>
                      <input
                        type="number"
                        className={styles.amountInput}
                        value={orderAmount}
                        onChange={(e) => setOrderAmount(Number(e.target.value))}
                        min={1}
                        max={25000}
                        step={50}
                        title="Order amount in dollars"
                      />
                      <span className={styles.amountInfo}>≈ {(orderAmount / orderModal.price).toFixed(1)} shares</span>
                    </div>
                  ) : (
                    <div className={styles.amountRow}>
                      <label className={styles.amountLabel}>Shares</label>
                      <input
                        type="number"
                        className={styles.amountInput}
                        value={orderQty}
                        onChange={(e) => setOrderQty(Number(e.target.value))}
                        min={1}
                        max={1000}
                        step={1}
                        title="Order quantity in shares"
                      />
                      <span className={styles.amountInfo}>≈ ${(orderQty * orderModal.price).toFixed(0)}</span>
                    </div>
                  )}

                  <div className={styles.quickAmounts}>
                    {[100, 250, 500, 1000, 2500].map((amt) => (
                      <button
                        key={amt}
                        className={styles.quickAmountBtn}
                        onClick={() => {
                          setOrderType('notional');
                          setOrderAmount(amt);
                        }}
                      >
                        ${amt}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.modalAccount}>
                  Account: <strong>{isLive ? 'LIVE' : 'PAPER'}</strong>
                  {account && (
                    <span>
                      {' '}
                      · Buying Power: $
                      {Number(account.buying_power).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  )}
                </div>

                {isLive && (
                  <label className={styles.liveCheckbox}>
                    <input
                      type="checkbox"
                      checked={liveConfirmed}
                      onChange={(e) => setLiveConfirmed(e.target.checked)}
                      aria-label="I understand this will trade with real money"
                    />
                    I understand this is a {isShort ? 'real-money SHORT' : 'real-money'} trade
                  </label>
                )}

                <div className={styles.modalBtns}>
                  <button
                    className={styles.cancelBtn}
                    onClick={() => {
                      setOrderModal(null);
                      setLiveConfirmed(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className={orderModal.side === 'buy' ? styles.confirmLongBtn : styles.confirmShortBtn}
                    onClick={handlePlaceOrder}
                    disabled={orderInProgress || (isLive && !liveConfirmed)}
                  >
                    {orderInProgress ? 'Placing...' : `Confirm ${actionLabel} ${orderModal.symbol}`}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Toast */}
      {toast && (
        <div
          className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}
          onClick={() => setToast(null)}
        >
          {toast.message}
        </div>
      )}
    </section>
  );
};

export default Scanner;
